import * as React from "react";
import { d365ScriptContent } from "../../providers/d365-script-provider/d365-script-provider";
import { useUserTracking } from "../user-tracking";

function useD365Form(props) {
  const { formId, onSubmit, onSubmitAll } = props;
  const { isReady, leadSource, contactSource, vorneDomain } =
    React.useContext(d365ScriptContent);
  const { getUTM, getConvertHistoricalData, session, version } =
    useUserTracking();
  const [isRendered, setIsRendered] = React.useState(false);

  const getHiddenFieldsMap = () => {
    const utm = getUTM();
    const freeTrial = new Map([
      ["#xl-trial-request-form input[data-field-name=ga-source]", utm?.source],
      ["#xl-trial-request-form input[data-field-name=ga-medium]", utm?.medium],
      [
        "#xl-trial-request-form input[data-field-name=ga-content]",
        utm?.content,
      ],
      [
        "#xl-trial-request-form input[data-field-name=ga-campaign]",
        utm?.campaign,
      ],
      [
        "#xl-trial-request-form input[data-field-name=geo-ip-continent]",
        session?.geo?.continent,
      ],
      [
        "#xl-trial-request-form input[data-field-name=geo-ip-country]",
        session?.geo?.country,
      ],
      [
        "#xl-trial-request-form input[data-field-name=geo-ip-isp]",
        session?.geo?.ispName,
      ],
      [
        "#xl-trial-request-form input[data-field-name=geo-ip-region]",
        session?.geo?.region,
      ],
      [
        "#xl-trial-request-form input[data-field-name=conversion-page-url]",
        session.history?.at(-2) ?? session.history?.at(-1),
      ],
      [
        "#xl-trial-request-form input[data-field-name=landing-page-url]",
        session?.initialLanding,
      ],
      [
        "#xl-trial-request-form input[data-field-name=referrer-page-url]",
        session?.initialReferrer ?? "",
      ],
    ]);
    const webinar = new Map([
      [
        "#xl-webinar-request-form input[data-field-name=ga-source]",
        utm?.source,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=ga-medium]",
        utm?.medium,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=ga-content]",
        utm?.content,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=ga-campaign]",
        utm?.campaign,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=geo-ip-continent]",
        session?.geo?.continent,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=geo-ip-country]",
        session?.geo?.country,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=geo-ip-isp]",
        session?.geo?.ispName,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=geo-ip-region]",
        session?.geo?.region,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=conversion-page-url]",
        session?.history?.at(-2) ?? session?.history?.at(-1),
      ],
      [
        "#xl-webinar-request-form input[data-field-name=landing-page-url]",
        session?.initialLanding,
      ],
      [
        "#xl-webinar-request-form input[data-field-name=referrer-page-url]",
        session?.initialReferrer ?? "",
      ],
    ]);
    const cssId = new Map([
      ["2a68a713-d820-ee11-9cbd-0022482b281a", "#email-sign-up-form"],
      ["273dca1f-d820-ee11-9cbd-0022482b281a", "#email-sign-up-form"],
      ["365e2877-8bb4-ee11-a569-002248292e69", "#email-sign-up-form"],
      ["1c6e64e5-f01e-ee11-9cbe-0022482b288e", "#email-sign-up-form"],
      ["6a3cbf30-fa85-ef11-ac21-6045bdd6ed67", "#lean-banner"],
      ["90b9e28e-5287-ef11-ac21-7c1e5201228e", "#oee-banner"],
    ]);

    const email = new Map([
      [
        `${cssId.get(formId)} select[data-field-name=contact-source]`,
        contactSource,
      ],
      [`${cssId.get(formId)} select[data-field-name=lead-source]`, leadSource],
      [
        `${cssId.get(formId)} input[data-field-name=last-name]`,
        `(${
          document.querySelector(
            `${cssId.get(formId)} input[data-field-name=email]`,
          )?.value
        })`,
      ],
      [`${cssId.get(formId)} input[data-field-name=ga-source]`, utm?.source],
      [`${cssId.get(formId)} input[data-field-name=ga-medium]`, utm?.medium],
      [`${cssId.get(formId)} input[data-field-name=ga-content]`, utm?.content],
      [
        `${cssId.get(formId)} input[data-field-name=ga-campaign]`,
        utm?.campaign,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=geo-ip-continent]`,
        session?.geo?.continent,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=geo-ip-country]`,
        session?.geo?.country,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=geo-ip-isp]`,
        session?.geo?.ispName,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=geo-ip-region]`,
        session?.geo?.region,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=conversion-page-url]`,
        session.history?.at(-2) ?? session.history?.at(-1),
      ],
      [
        `${cssId.get(formId)} input[data-field-name=landing-page-url]`,
        session?.initialLanding,
      ],
      [
        `${cssId.get(formId)} input[data-field-name=referrer-page-url]`,
        session?.initialReferrer ?? "",
      ],
      [`${cssId.get(formId)} input[data-field-name=vorne-domain]`, vorneDomain],
    ]);
    const freeTools = new Map([
      [
        "#download-free-tools-form select[data-field-name=contact-source]",
        contactSource,
      ],
      [
        "#download-free-tools-form select[data-field-name=lead-source]",
        leadSource,
      ],
      [
        "#download-free-tools-form input[data-field-name=last-name]",
        `(${
          document.querySelector(
            "#download-free-tools-form input[data-field-name=email]",
          )?.value
        })`,
      ],
      [
        "#download-free-tools-form input[data-field-name=ga-source]",
        utm?.source,
      ],
      [
        "#download-free-tools-form input[data-field-name=ga-medium]",
        utm?.medium,
      ],
      [
        "#download-free-tools-form input[data-field-name=ga-content]",
        utm?.content,
      ],
      [
        "#download-free-tools-form input[data-field-name=ga-campaign]",
        utm?.campaign,
      ],
      [
        "#download-free-tools-form input[data-field-name=geo-ip-continent]",
        session?.geo?.continent,
      ],
      [
        "#download-free-tools-form input[data-field-name=geo-ip-country]",
        session?.geo?.country,
      ],
      [
        "#download-free-tools-form input[data-field-name=geo-ip-isp]",
        session?.geo?.ispName,
      ],
      [
        "#download-free-tools-form input[data-field-name=geo-ip-region]",
        session?.geo?.region,
      ],
      [
        "#download-free-tools-form input[data-field-name=conversion-page-url]",
        session.history?.at(-2) ?? session.history?.at(-2),
      ],
      [
        "#download-free-tools-form input[data-field-name=landing-page-url]",
        session?.initialLanding,
      ],
      [
        "#download-free-tools-form input[data-field-name=referrer-page-url]",
        session?.initialReferrer ?? "",
      ],
      [
        "#download-free-tools-form input[data-field-name=vorne-domain]",
        vorneDomain,
      ],
    ]);
    const whatComesNext = new Map([
      [
        "#what-comes-next-form select[data-field-name=contact-source]",
        contactSource,
      ],
      ["#what-comes-next-form select[data-field-name=lead-source]", leadSource],
      [
        "#what-comes-next-form input[data-field-name=last-name]",
        `(${
          document.querySelector(
            "#what-comes-next-form input[data-field-name=email]",
          )?.value
        })`,
      ],
      ["#what-comes-next-form input[data-field-name=ga-source]", utm?.source],
      ["#what-comes-next-form input[data-field-name=ga-medium]", utm?.medium],
      ["#what-comes-next-form input[data-field-name=ga-content]", utm?.content],
      [
        "#what-comes-next-form input[data-field-name=ga-campaign]",
        utm?.campaign,
      ],
      [
        "#what-comes-next-form input[data-field-name=geo-ip-continent]",
        session?.geo?.continent,
      ],
      [
        "#what-comes-next-form input[data-field-name=geo-ip-country]",
        session?.geo?.country,
      ],
      [
        "#what-comes-next-form input[data-field-name=geo-ip-isp]",
        session?.geo?.ispName,
      ],
      [
        "#what-comes-next-form input[data-field-name=geo-ip-region]",
        session?.geo?.region,
      ],
      [
        "#what-comes-next-form input[data-field-name=conversion-page-url]",
        session.history?.at(-2) ?? session.history?.at(-2),
      ],
      [
        "#what-comes-next-form input[data-field-name=landing-page-url]",
        session?.initialLanding,
      ],
      [
        "#what-comes-next-form input[data-field-name=referrer-page-url]",
        session?.initialReferrer ?? "",
      ],
      [
        "#what-comes-next-form input[data-field-name=vorne-domain]",
        vorneDomain,
      ],
    ]);
    const lut = new Map([
      ["cd0255bf-d415-ee11-8f6e-0022482b2538", freeTrial],
      ["1514fe80-8aed-ee11-a204-000d3a14dcb8", freeTrial],
      ["109757fc-7917-ee11-8f6e-0022482b288e", webinar], //8
      ["2a68a713-d820-ee11-9cbd-0022482b281a", email], //3
      ["273dca1f-d820-ee11-9cbd-0022482b281a", email], //4
      ["365e2877-8bb4-ee11-a569-002248292e69", email], // 2
      ["1c6e64e5-f01e-ee11-9cbe-0022482b288e", email], // 5
      ["6a3cbf30-fa85-ef11-ac21-6045bdd6ed67", email],
      ["90b9e28e-5287-ef11-ac21-7c1e5201228e", email],
      ["125f4231-9a1e-ee11-9cbd-002248292e69", freeTools], // 1
      ["b2cbfc80-c822-ee11-9cbd-002248292e69", whatComesNext], //6
      ["754ff099-c822-ee11-9cbd-002248292e69", whatComesNext], // 7
    ]);
    return lut.get(formId);
  };

  const onFormSubmit = (event) => {
    if (event.formPageId === formId) {
      const hiddenFields = getHiddenFieldsMap();
      hiddenFields.forEach((value, key) => {
        const field = document.querySelector(key);
        if (field) {
          field.value = value;
        }
      });
    }
  };

  const createFreeTrialEmail = () => {
    const utm = getUTM();
    const conversionTracking = getConvertHistoricalData()
      .map((experiment) => {
        return `
      <div>
      <p style="padding: 0px; margin: 0px;"><b>Experiment:</b> ${
        experiment.experiment_name
      }</p>
      <p style="padding: 0px; margin: 0px;"><b>Variant:</b> ${
        experiment.variation_name
      }</p>
      <p style="padding: 0px; margin: 0px;"><b>Goals Triggered:</b></p>
      <ul style="margin: 0px;">
      ${
        experiment.goals_triggered.length > 0
          ? experiment.goals_triggered
              .filter((goal) => goal !== undefined)
              .map((goal) => `<li>${goal}</li>`)
              .join("")
          : "<li>No goals triggered</li>"
      }
      </ul>
      <br />
      </div>
    `;
      })
      .join("");
    const hasTracking = conversionTracking.length !== 0;
    return `
      <style>
        table {
          min-width: 320px;
          margin-bottom: 20px;
          text-align: left;
        }
        th,
        td {
          border-radius: 5px;
        }
        .blue {
          background-color: #106fc0;
          color: white;
        }
        .gray {
          background-color: #464646;
          color: white;
        }
        .light-gray {
          background-color: #f2f2f2;
        }
        .light-blue {
          background-color: #f0f5f9;
        }
      </style>
      <main>
        <p>Great news - we have a new lead! It can take up to 10 minutes for Dynamics 365 to generate the associated records.</p>
        <table id="contact">
        <thead>
          <tr class="blue">
            <th colspan="2">Contact Information</th>
          </tr>
        </thead>
        <tbody>
          <tr class="light-blue">
            <td>First Name</td>
            <td>${
              document.querySelector(
                "#xl-trial-request-form input[data-field-name=first-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Last Name</td>
            <td>${
              document.querySelector(
                "#xl-trial-request-form input[data-field-name=last-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-blue">
            <td>Company</td>
            <td>${
              document.querySelector(
                "#xl-trial-request-form input[data-field-name=company-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Email</td>
            <td>${
              document.querySelector("input[data-field-name=email]").value
            }</td>
          </tr>
          <tr class="light-blue">
            <td>Phone Number</td>
            <td>${
              document.querySelector(
                "#xl-trial-request-form input[data-field-name=phone-number]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Application</td>
            <td>${
              document.querySelector(
                "#xl-trial-request-form textarea[data-field-name=application]",
              ).value
            }</td>
          </tr>
        </tbody>
      </table>
      <table id="geographical">
      <thead>
        <tr  class="blue">
          <th colspan="2">Geographical Information</th>
        </tr>
      </thead>
      <tbody>
        <tr class="light-blue">
          <td>Region</td>
          <td>${session?.geo?.region}</td>
        </tr>
        <tr class="light-gray">
          <td>Country</td>
          <td>${session?.geo?.country}</td>
        </tr>
        <tr class="light-blue">
          <td>Continent</td>
          <td>${session?.geo?.continent}</td>
        </tr>
        <tr class="light-gray">
          <td>ISP</td>
          <td>${session?.geo?.ispName}</td>
        </tr>
      </tbody>
    </table>
    <table id="utm">
    <thead>
      <tr class="blue">
        <th colspan="2">UTM Information</th>
      </tr>
    </thead>
    <tbody>
      <tr class="light-blue">
        <td>Source</td>
        <td>${utm?.source}</td>
      </tr>
      <tr class="light-gray">
        <td>Medium</td>
        <td>${utm?.medium}</td>
      </tr>
      <tr class="light-blue">
        <td>Campaign</td>
        <td>${utm?.campaign}</td>
      </tr>
      <tr class="light-gray">
        <td>Content</td>
        <td>${utm?.content}</td>
      </tr>
    </tbody>
  </table>
  <table id="user-tracking">
    <thead>
      <tr class="blue">
        <th colspan="2">Tracking Information</th>
      </tr>
      <tr>
        <th class="blue">Property Name</th>
        <th class="gray">Session</th>
      </tr>
    </thead>
    <tbody>
      <tr class="light-blue">
        <td>Initial Referrer</td>
        <td>${session.initialReferrer ?? ""}</td>
      </tr>
      <tr class="light-gray">
        <td>Initial Landing</td>
        <td>${session.initialLanding}</td>
      </tr>
      <tr class="light-blue">
        <td>Vorne Referrer</td>
        <td>${session.vorneReferrer ?? ""}</td>
      </tr>
      <tr class="light-gray">
        <td>Vorne Landing</td>
        <td>${session.vorneLanding ?? ""}</td>
      </tr>
      <tr class="light-blue">
        <td>Conversion Page URL</td>
        <td>${session.history.at(-2) ?? session.history.at(-1)}</td>
      </tr>
      <tr class="light-gray">
        <td>User Agent</td>
        <td>${navigator.userAgent}</td>
      </tr>
      <tr class="light-blue">
        <td>Version</td>
        <td>${version}</td>
      </tr>
    </tbody>
  </table>
  <h2>Convert Historical Data</h2>
  ${hasTracking ? conversionTracking : "None"}
  <h2>Visited Pages</h2>
  <ol>
    ${[
      session.initialReferrer == undefined || session.initialReferrer == ""
        ? "None"
        : session.initialReferrer,
      ...session.history,
    ]
      .filter((item) => item !== null)
      .map((url) => `<li>${url}</li>`)
      .join(" ")}
  </ol>
  </main>
    `;
  };

  const createWebinarEmail = () => {
    const utm = getUTM();
    const conversionTracking = getConvertHistoricalData()
      .map((experiment) => {
        return `
      <div>
      <p style="padding: 0px; margin: 0px;"><b>Experiment:</b> ${
        experiment.experiment_name
      }</p>
      <p style="padding: 0px; margin: 0px;"><b>Variant:</b> ${
        experiment.variation_name
      }</p>
      <p style="padding: 0px; margin: 0px;"><b>Goals Triggered:</b></p>
      <ul style="margin: 0px;">
      ${
        experiment.goals_triggered.length > 0
          ? experiment.goals_triggered
              .filter((goal) => goal !== undefined)
              .map((goal) => `<li>${goal}</li>`)
              .join("")
          : "<li>No goals triggered</li>"
      }
      </ul>
      <br />
      </div>
    `;
      })
      .join("");

    const hasTracking = conversionTracking.length !== 0;

    return `
      <style>
        table {
          min-width: 320px;
          margin-bottom: 20px;
          text-align: left;
        }
        th,
        td {
          border-radius: 5px;
        }
        .blue {
          background-color: #106fc0;
          color: white;
        }
        .gray {
          background-color: #464646;
          color: white;
        }
        .light-gray {
          background-color: #f2f2f2;
        }
        .light-blue {
          background-color: #f0f5f9;
        }
      </style>
      <main>
        <p>Great news - we have a new lead! It can take up to 10 minutes for Dynamics 365 to generate the associated records.</p>
        <table id="contact">
        <thead>
          <tr class="blue">
            <th colspan="2">Contact Information</th>
          </tr>
        </thead>
        <tbody>
          <tr class="light-blue">
            <td>First Name</td>
            <td>${
              document.querySelector(
                "#xl-webinar-request-form input[data-field-name=first-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Last Name</td>
            <td>${
              document.querySelector(
                "#xl-webinar-request-form input[data-field-name=last-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-blue">
            <td>Company</td>
            <td>${
              document.querySelector(
                "#xl-webinar-request-form input[data-field-name=company-name]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Email</td>
            <td>${
              document.querySelector("input[data-field-name=email]").value
            }</td>
          </tr>
          <tr class="light-blue">
            <td>Phone Number</td>
            <td>${
              document.querySelector(
                "#xl-webinar-request-form input[data-field-name=phone-number]",
              ).value
            }</td>
          </tr>
          <tr class="light-gray">
            <td>Application</td>
            <td>${
              document.querySelector(
                "#xl-webinar-request-form textarea[data-field-name=application]",
              ).value
            }</td>
          </tr>
        </tbody>
      </table>
      <table id="geographical">
      <thead>
        <tr  class="blue">
          <th colspan="2">Geographical Information</th>
        </tr>
      </thead>
      <tbody>
        <tr class="light-blue">
          <td>Region</td>
          <td>${session?.geo?.region}</td>
        </tr>
        <tr class="light-gray">
          <td>Country</td>
          <td>${session?.geo?.country}</td>
        </tr>
        <tr class="light-blue">
          <td>Continent</td>
          <td>${session?.geo?.continent}</td>
        </tr>
        <tr class="light-gray">
          <td>ISP</td>
          <td>${session?.geo?.ispName}</td>
        </tr>
      </tbody>
    </table>
    <table id="utm">
    <thead>
      <tr class="blue">
        <th colspan="2">UTM Information</th>
      </tr>
    </thead>
    <tbody>
      <tr class="light-blue">
        <td>Source</td>
        <td>${utm?.source}</td>
      </tr>
      <tr class="light-gray">
        <td>Medium</td>
        <td>${utm?.medium}</td>
      </tr>
      <tr class="light-blue">
        <td>Campaign</td>
        <td>${utm?.campaign}</td>
      </tr>
      <tr class="light-gray">
        <td>Content</td>
        <td>${utm?.content}</td>
      </tr>
    </tbody>
  </table>
  <table id="user-tracking">
    <thead>
      <tr class="blue">
        <th colspan="2">Tracking Information</th>
      </tr>
      <tr>
        <th class="blue">Property Name</th>
        <th class="gray">Session</th>
      </tr>
    </thead>
    <tbody>
      <tr class="light-blue">
        <td>Initial Referrer</td>
        <td>${session.initialReferrer ?? ""}</td>
      </tr>
      <tr class="light-gray">
        <td>Initial Landing</td>
        <td>${session.initialLanding}</td>
      </tr>
      <tr class="light-blue">
        <td>Vorne Referrer</td>
        <td>${session.vorneReferrer ?? ""}</td>
      </tr>
      <tr class="light-gray">
        <td>Vorne Landing</td>
        <td>${session.vorneLanding ?? ""}</td>
      </tr>
      <tr class="light-blue">
        <td>Conversion Page URL</td>
        <td>${session.history.at(-2) ?? session.history.at(-1)}</td>
      </tr>
    </tbody>
  </table>
  <h2>Convert Historical Data</h2>
  ${hasTracking ? conversionTracking : "None"}
  <h2>Visited Pages</h2>
  <ol>
    ${[
      session.initialReferrer == undefined || session.initialReferrer == ""
        ? "None"
        : session.initialReferrer,
      ...session.history,
    ]
      .filter((item) => item !== null)
      .map((url) => `<li>${url}</li>`)
      .join(" ")}
  </ol>
  </main>
    `;
  };

  const afterFormSubmit = (event) => {
    // Free tools
    if (event.formPageId === formId) {
      onSubmit?.();
    }
    // Email
    if (
      event.formPageId === "cd0255bf-d415-ee11-8f6e-0022482b2538" ||
      event.formPageId === "1514fe80-8aed-ee11-a204-000d3a14dcb8"
    ) {
      fetch(
        "https://prod-148.westus.logic.azure.com:443/workflows/7bc643ad5d364269befeff157d6c10c7/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=-UVSdBJNwY88kf72Z9TSA0Yj9RTHmYPjQzlw8nHA59A",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: createFreeTrialEmail(),
          }),
        },
      );
    }

    // Email
    if (event.formPageId === "109757fc-7917-ee11-8f6e-0022482b288e") {
      fetch(
        "https://prod-148.westus.logic.azure.com:443/workflows/7bc643ad5d364269befeff157d6c10c7/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=-UVSdBJNwY88kf72Z9TSA0Yj9RTHmYPjQzlw8nHA59A",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: createWebinarEmail(),
          }),
        },
      );
    }

    onSubmitAll?.();
  };

  const afterFormRender = (event) => {
    if (event.formPageId === formId) {
      setIsRendered(true);
    }
  };

  React.useEffect(() => {
    if (isReady) {
      window?.MsCrmMkt?.MsCrmFormLoader?.on("formSubmit", onFormSubmit);
      window?.MsCrmMkt?.MsCrmFormLoader?.on("afterFormSubmit", afterFormSubmit);
      window?.MsCrmMkt?.MsCrmFormLoader?.on("afterFormRender", afterFormRender);

      return () => {
        window?.MsCrmMkt?.MsCrmFormLoader?.off("formSubmit");
        window?.MsCrmMkt?.MsCrmFormLoader?.off("afterFormSubmit");
        window?.MsCrmMkt?.MsCrmFormLoader?.off(
          "afterFormRender",
          afterFormRender,
        );
      };
    }
  }, [isReady, onFormSubmit, afterFormSubmit]);

  return { isRendered };
}

export default useD365Form;
